import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Upload, Check, X, Music, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAudioUpload = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [audioStatus, setAudioStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  const fetchAudioStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/audio/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAudioStatus(res.data.themes);
    } catch (error) {
      toast.error('Erreur lors du chargement du statut audio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudioStatus();
  }, [token]);

  const handleUpload = async (slug, mode, file) => {
    if (!file) return;
    
    const uploadKey = `${slug}-${mode}`;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('theme_slug', slug);
    formData.append('mode', mode);
    
    try {
      await axios.post(`${API_URL}/admin/audio/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Audio ${slug}-${mode} uploadé avec succès !`);
      fetchAudioStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleDelete = async (slug, mode) => {
    if (!window.confirm(`Supprimer ${slug}-${mode}.mp3 ?`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/audio/${slug}/${mode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fichier supprimé');
      fetchAudioStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const triggerFileInput = (slug, mode) => {
    const key = `${slug}-${mode}`;
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].click();
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const testAudio = async (slug, mode) => {
    try {
      const res = await axios.get(`${API_URL}/audio/stream/${slug}/${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      if (res.status === 200) {
        toast.success(`Audio ${slug}-${mode} OK !`);
        return true;
      }
    } catch (error) {
      toast.error(`Audio ${slug}-${mode} non trouvé`);
      return false;
    }
  };

  // Component pour chaque mode audio (Zen ou Movement)
  const AudioModeCard = ({ theme, mode, modeData, bgClass }) => {
    const uploadKey = `${theme.slug}-${mode}`;
    const isUploading = uploading[uploadKey];
    const modeLabel = mode === 'zen' ? 'Zen' : 'Mouvement';
    const isEmptyFile = modeData.exists && modeData.size === 0;
    const hasRealFile = modeData.exists && modeData.size > 0;
    
    return (
      <div className={`${bgClass} rounded-lg p-3`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-sage-700">{modeLabel}</span>
          {hasRealFile ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : isEmptyFile ? (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Vide</span>
          ) : (
            <X className="w-4 h-4 text-red-500" />
          )}
        </div>
        
        {/* Input file toujours présent (caché) */}
        <input
          type="file"
          accept=".mp3,audio/mpeg"
          ref={el => fileInputRefs.current[uploadKey] = el}
          onChange={(e) => handleUpload(theme.slug, mode, e.target.files[0])}
          className="hidden"
          data-testid={`input-${theme.slug}-${mode}`}
        />
        
        {hasRealFile ? (
          <>
            <p className="text-xs text-sage-600 mb-1">
              {modeData.expected} ({formatSize(modeData.size)})
            </p>
            {modeData.duration_formatted && (
              <p className="text-xs text-sage-500 mb-2">
                ⏱ Durée : {modeData.duration_formatted}
              </p>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => testAudio(theme.slug, mode)}
                className="h-7 px-2 text-xs flex-1"
                data-testid={`test-${theme.slug}-${mode}`}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Tester
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => triggerFileInput(theme.slug, mode)}
                disabled={isUploading}
                className="h-7 px-2 text-xs"
                title="Remplacer le fichier"
                data-testid={`replace-${theme.slug}-${mode}`}
              >
                <Upload className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(theme.slug, mode)}
                className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-${theme.slug}-${mode}`}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </>
        ) : isEmptyFile ? (
          <>
            <p className="text-xs text-amber-600 mb-2">
              Fichier placeholder (0 octets) - Uploadez le vrai fichier
            </p>
            <Button
              size="sm"
              onClick={() => triggerFileInput(theme.slug, mode)}
              disabled={isUploading}
              className="h-8 w-full bg-amber-500 hover:bg-amber-600 text-white text-xs"
              data-testid={`upload-${theme.slug}-${mode}`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Upload...
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" />
                  Remplacer par le vrai MP3
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs text-red-500 mb-2">Fichier manquant</p>
            <Button
              size="sm"
              onClick={() => triggerFileInput(theme.slug, mode)}
              disabled={isUploading}
              className="h-8 w-full bg-sage-600 hover:bg-sage-600 text-white text-xs"
              data-testid={`upload-${theme.slug}-${mode}`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Upload...
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" />
                  Uploader MP3
                </>
              )}
            </Button>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50 pb-24" data-testid="admin-audio-page">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            className="text-white hover:bg-sage-600"
            data-testid="admin-audio-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-serif font-light">Fichiers Audio</h1>
            <p className="text-white/70 text-sm">Uploadez et gérez les fichiers audio du programme</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 mb-4">
          <h3 className="font-medium text-sage-800 mb-2">Comment ça marche</h3>
          <ul className="text-sm text-sage-700 space-y-1 list-disc list-inside">
            <li>Cliquez sur <strong>"Uploader MP3"</strong> pour ajouter un fichier manquant</li>
            <li>Le fichier sera automatiquement renommé au bon format</li>
            <li>Utilisez <strong>"Tester"</strong> pour vérifier que l'audio fonctionne</li>
          </ul>
        </div>
      </div>

      {/* Summary */}
      <div className="px-6 pb-4">
        <div className="bg-white rounded-xl border border-sand-200 p-4 flex justify-between items-center">
          <div>
            <p className="text-sage-700 font-medium">Statut des fichiers</p>
            <p className="text-sm text-sage-500">
              {audioStatus.filter(t => t.zen.exists).length + audioStatus.filter(t => t.movement.exists).length} / {audioStatus.length * 2} fichiers présents
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setLoading(true); fetchAudioStatus(); }}
            data-testid="refresh-status"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Liste des thèmes */}
      <div className="px-6 space-y-3">
        {audioStatus.map((theme) => (
          <div 
            key={theme.slug}
            className="bg-white rounded-xl p-4 border border-sand-200"
            data-testid={`theme-card-${theme.slug}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  theme.zen.exists && theme.movement.exists 
                    ? 'bg-green-100' 
                    : theme.zen.exists || theme.movement.exists 
                      ? 'bg-amber-100' 
                      : 'bg-red-100'
                }`}>
                  <Music className={`w-5 h-5 ${
                    theme.zen.exists && theme.movement.exists 
                      ? 'text-green-600' 
                      : theme.zen.exists || theme.movement.exists 
                        ? 'text-amber-600' 
                        : 'text-red-500'
                  }`} />
                </div>
                <div>
                  <h3 className="font-medium text-sage-700">{theme.title}</h3>
                  <p className="text-xs text-sage-500">slug: {theme.slug}</p>
                </div>
              </div>
              {theme.zen.exists && theme.movement.exists && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  Complet
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <AudioModeCard 
                theme={theme} 
                mode="zen" 
                modeData={theme.zen} 
                bgClass="bg-sage-50" 
              />
              <AudioModeCard 
                theme={theme} 
                mode="movement" 
                modeData={theme.movement} 
                bgClass="bg-amber-50" 
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-6">
        <div className="bg-sage-100 rounded-xl p-4 text-center">
          <p className="text-sage-700">
            <strong>{audioStatus.length}</strong> thèmes × <strong>2</strong> versions = <strong>{audioStatus.length * 2}</strong> fichiers audio attendus
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAudioUpload;
